// packages/utils/api-client/src/lib/perspectives/assignSubjectToPerspective.ts

import { ApiClient } from '@center-technologies/shared-api-clients';

export interface AssignSubjectToPerspectiveParams {
  perspectiveId?: string;              // Already-known perspective ID
  perspectiveSystemName?: string;      // Or lookup by system name + sphereId
  sphereId?: string;                   // Required if using system name
  subjectId: string;                   // ID of the subject being assigned
  urgency?: number;                    // Optional urgency level
}

export interface AssignSubjectToPerspectiveResponse {
  success: boolean;
  message: string;
  error?: string;
}

interface PerspectiveLookupResponse {
  id: string;
}

/**
 * Look up a perspective ID by system name.
 */
async function getPerspectiveIdFromSystemName(
  apiClient: ApiClient,
  sphereId: string,
  systemName: string
): Promise<string> {
  const data = await apiClient.get<PerspectiveLookupResponse>(
    `/spheres/${encodeURIComponent(sphereId)}/perspective/${encodeURIComponent(systemName)}`,
    undefined,
    /* authRequired */ true
  );
  return data.id;
}

/**
 * Assigns a subject to a perspective.
 *
 * @param apiClient – your pre-configured ApiClient
 * @param params – assignment details
 */
export async function assignSubjectToPerspective(
  apiClient: ApiClient,
  params: AssignSubjectToPerspectiveParams
): Promise<AssignSubjectToPerspectiveResponse> {
  let { perspectiveId } = params;
  const { perspectiveSystemName, sphereId, subjectId, urgency = 2 } = params;

  // Resolve by system name if needed
  if (!perspectiveId && perspectiveSystemName && sphereId) {
    try {
      perspectiveId = await getPerspectiveIdFromSystemName(
        apiClient,
        sphereId,
        perspectiveSystemName
      );
    } catch (err: any) {
      return {
        success: false,
        message: 'Could not resolve perspective ID',
        error: err.message,
      };
    }
  }

  if (!perspectiveId) {
    return {
      success: false,
      message: 'Must provide either perspectiveId or (perspectiveSystemName + sphereId)',
    };
  }

  try {
    await apiClient.post<void>(
      `/perspectives/${encodeURIComponent(perspectiveId)}/assign-subject`,
      { subjectId, urgency },
      undefined,
      /* authRequired */ true
    );
    return { success: true, message: 'Assigned successfully' };
  } catch (err: any) {
    console.error('assignSubjectToPerspective error', err);
    return {
      success: false,
      message: err.message ?? 'Assignment failed',
      error: err.response?.data?.error,
    };
  }
}
